import { useState, useEffect } from 'react';
import { Container, Card, Alert } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { forgotPassword, resetPassword } from '../../api/authApi';

const cyan = '#00e5ff';

const inputStyle = {
  width: '100%',
  background: 'rgba(0,229,255,0.04)',
  border: '1px solid rgba(0,229,255,0.2)',
  borderRadius: 8,
  padding: '10px 14px',
  color: '#fff',
  fontSize: 14,
  outline: 'none',
  marginTop: 4,
};

const labelStyle = {
  color: '#888',
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
};

const btnStyle = (disabled) => ({
  width: '100%',
  padding: '11px',
  borderRadius: 8,
  border: 'none',
  background: disabled ? 'rgba(255,255,255,0.06)' : cyan,
  color: disabled ? '#555' : '#000',
  fontWeight: 700,
  fontSize: 15,
  cursor: disabled ? 'not-allowed' : 'pointer',
  marginTop: 8,
});

const cardStyle = {
  background: 'radial-gradient(ellipse at 100% 0%, rgba(0,229,255,0.06) 0%, #0d0d0d 65%)',
  border: '1px solid rgba(0,229,255,0.15)',
  color: '#fff',
  borderRadius: 14,
  padding: '32px 28px',
  width: '100%',
  maxWidth: 420,
};

const ForgotPassword = () => {
  const navigate = useNavigate();

  // Step 1 state
  const [email, setEmail]         = useState('');
  const [sending, setSending]     = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);

  // Step 2 state
  const [step, setStep]           = useState(1); // 1 = email, 2 = otp + new pw
  const [otp, setOtp]             = useState('');
  const [newPassword, setNewPassword]         = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetting, setResetting] = useState(false);

  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');

  // OTP resend timer
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
    if (resendTimer === 0 && step === 2) setCanResend(true);
  }, [resendTimer, step]);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError('');
    setSending(true);
    try {
      await forgotPassword({ email });
      setStep(2);
      setResendTimer(30);
      setCanResend(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleResendOTP = async (e) => {
    e.preventDefault();
    setError('');
    setSending(true);
    try {
      await forgotPassword({ email });
      setResendTimer(30);
      setCanResend(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend OTP. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) return setError('Passwords do not match.');
    if (newPassword.length < 6) return setError('Password must be at least 6 characters.');
    setResetting(true);
    try {
      await resetPassword({ email, otp, newPassword });
      setSuccess('Password reset successfully! Redirecting to login...');
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err.response?.data?.message || 'Reset failed. Please try again.');
    } finally {
      setResetting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px' }}>
      <div style={cardStyle}>

        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          <h5 style={{ color: '#fff', fontWeight: 700, marginBottom: 4 }}>
            {step === 1 ? 'Forgot Password' : 'Reset Password'}
          </h5>
          <div style={{ color: '#666', fontSize: 13 }}>
            {step === 1
              ? 'Enter your registered email to receive an OTP.'
              : <>OTP sent to <span style={{ color: cyan }}>{email}</span>. Enter it below along with your new password.</>
            }
          </div>
        </div>

        {error   && <Alert style={{ background: 'rgba(200,50,50,0.12)', border: '1px solid rgba(200,50,50,0.3)', color: '#ff6666', fontSize: 13, borderRadius: 8, padding: '10px 14px' }}>{error}</Alert>}
        {success && <Alert style={{ background: 'rgba(102,255,153,0.1)', border: '1px solid rgba(102,255,153,0.3)', color: '#66ff99', fontSize: 13, borderRadius: 8, padding: '10px 14px' }}>{success}</Alert>}

        {/* Step 1 — Email */}
        {step === 1 && (
          <form onSubmit={handleSendOTP}>
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                placeholder="you@example.com"
                style={inputStyle}
                required
                autoFocus
              />
            </div>
            <button type="submit" style={btnStyle(sending || !email)} disabled={sending || !email}>
              {sending ? 'Sending OTP...' : 'Send OTP'}
            </button>
          </form>
        )}

        {/* Step 2 — OTP + New Password */}
        {step === 2 && (
          <form onSubmit={handleReset}>
            <div style={{ marginBottom: 18 }}>
              <label style={labelStyle}>OTP (6-digit)</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={otp}
                onChange={(e) => { setOtp(e.target.value.replace(/\D/g, '')); setError(''); }}
                placeholder="Enter OTP"
                style={{ ...inputStyle, fontSize: 22, fontWeight: 700, letterSpacing: 8, textAlign: 'center' }}
                required
                autoFocus
              />
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={labelStyle}>New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                placeholder="Min. 6 characters"
                style={inputStyle}
                required
              />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Re-enter New Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                placeholder="Confirm password"
                style={{
                  ...inputStyle,
                  borderColor: confirmPassword && confirmPassword !== newPassword
                    ? 'rgba(200,50,50,0.5)'
                    : 'rgba(0,229,255,0.2)',
                }}
                required
              />
              {confirmPassword && confirmPassword !== newPassword && (
                <div style={{ color: '#ff6666', fontSize: 11, marginTop: 4 }}>Passwords do not match.</div>
              )}
            </div>
            <button
              type="submit"
              style={btnStyle(resetting || otp.length < 6 || !newPassword || !confirmPassword)}
              disabled={resetting || otp.length < 6 || !newPassword || !confirmPassword}
            >
              {resetting ? 'Resetting...' : 'Reset Password'}
            </button>

            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={handleResendOTP}
                disabled={!canResend || sending}
                style={{ flex: 1, minWidth: 120, padding: '8px 12px', borderRadius: 6, border: 'none', background: canResend ? cyan : 'rgba(255,255,255,0.06)', color: canResend ? '#000' : '#555', fontSize: 12, fontWeight: 600, cursor: canResend ? 'pointer' : 'not-allowed' }}
              >
                {canResend ? 'Resend OTP' : `Wait ${resendTimer}s`}
              </button>
              <button
                type="button"
                onClick={() => { setStep(1); setOtp(''); setNewPassword(''); setConfirmPassword(''); setError(''); setResendTimer(0); setCanResend(false); }}
                style={{ flex: 1, minWidth: 120, padding: '8px 12px', borderRadius: 6, border: 'none', background: 'rgba(255,255,255,0.06)', color: '#555', fontSize: 12, cursor: 'pointer' }}
              >
                Change Email
              </button>
            </div>
          </form>
        )}

        <div style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: '#555' }}>
          <Link to="/login" style={{ color: cyan, textDecoration: 'none' }}>← Back to Login</Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
